import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdminUserDto, BugReportDto, adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminBugReportsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminBugReportsScreen: React.FC<AdminBugReportsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      success: theme.success,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<BugReportDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BugReportDto | null>(null);
  const previewId = preview?.id ?? null;
  const [previewMaximized, setPreviewMaximized] = useState(false);
  const [previewSize, setPreviewSize] = useState({ width: 900, height: 640 });
  const [previewResizing, setPreviewResizing] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [nextPreviewImageIndex, setNextPreviewImageIndex] = useState(0);
  const previewResizeStateRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const previewContentRef = useRef<HTMLDivElement | null>(null);

  const [filters, setFilters] = useState({
    username: '',
    category: '',
    title: '',
    status: 'all' as 'all' | 'open' | 'resolved',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger' | 'success', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    if (variant === 'success') return { ...base, border: 'none', backgroundColor: colors.success, color: '#fff' };
    return base;
  };

  const stripHtml = (html: string): string =>
    String(html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminApi.listAdminBugReports(token, { take: 1000 });
      setItems(list);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading bug reports');
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await adminApi.listUsers(token);
        if (cancelled) return;
        setUsers(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setUsers(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  useEffect(() => {
    if (previewId == null) return;
    setPreviewMaximized(false);
    setNextPreviewImageIndex(0);
    if (typeof window === 'undefined') return;

    const maxWidth = Math.max(540, window.innerWidth - 32);
    const maxHeight = Math.max(380, window.innerHeight - 32);
    setPreviewSize({
      width: Math.min(960, maxWidth),
      height: Math.min(720, maxHeight),
    });
  }, [previewId]);

  useEffect(() => {
    if (!previewResizing) return;

    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

    const onMouseMove = (ev: MouseEvent) => {
      const state = previewResizeStateRef.current;
      if (!state) return;

      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : state.startWidth;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : state.startHeight;

      const maxWidth = Math.max(540, viewportWidth - 32);
      const maxHeight = Math.max(380, viewportHeight - 32);
      const minWidth = 540;
      const minHeight = 380;

      const nextWidth = clamp(state.startWidth + (ev.clientX - state.startX), minWidth, maxWidth);
      const nextHeight = clamp(state.startHeight + (ev.clientY - state.startY), minHeight, maxHeight);

      setPreviewSize({ width: nextWidth, height: nextHeight });
    };

    const onMouseUp = () => {
      setPreviewResizing(false);
      previewResizeStateRef.current = null;
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nwse-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [previewResizing]);

  const startPreviewResize = (ev: React.MouseEvent<HTMLDivElement>) => {
    if (ev.button !== 0 || previewMaximized) return;
    ev.preventDefault();
    ev.stopPropagation();
    previewResizeStateRef.current = {
      startX: ev.clientX,
      startY: ev.clientY,
      startWidth: previewSize.width,
      startHeight: previewSize.height,
    };
    setPreviewResizing(true);
  };

  const copyPreviewContent = async () => {
    if (!preview) return;

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });

    const buildRichHtmlWithEmbeddedImages = async (): Promise<string> => {
      if (typeof window === 'undefined') {
        return `
          <div>
            <h3>${preview.title || ''}</h3>
            <div>${preview.username || ''} | ${preview.category || ''} | ${preview.createdAtUtc || ''}</div>
            <div>${preview.pageUrl ? `Page: ${preview.pageUrl}` : 'Page: -'}</div>
            <hr />
            <div>${preview.html || ''}</div>
          </div>
        `;
      }

      const parser = new DOMParser();
      const sourceHtml = previewContentRef.current?.innerHTML || preview.html || '';
      const doc = parser.parseFromString(`<div id="copy-root">${sourceHtml}</div>`, 'text/html');
      const root = doc.getElementById('copy-root');
      if (!root) return sourceHtml;

      const clonedImages = Array.from(root.querySelectorAll('img'));
      const liveImages = Array.from(previewContentRef.current?.querySelectorAll('img') || []);

      const toDataUrlFromLiveImage = async (liveImg?: HTMLImageElement, fallbackSrc?: string): Promise<string | null> => {
        const src = String(liveImg?.currentSrc || liveImg?.src || fallbackSrc || '').trim();
        if (!src) return null;
        if (src.startsWith('data:')) return src;

        try {
          const response = await fetch(src, {
            mode: 'cors',
            credentials: 'include',
            cache: 'force-cache',
          });
          if (response.ok) {
            const blob = await response.blob();
            const dataUrl = await blobToDataUrl(blob);
            if (dataUrl) return dataUrl;
          }
        } catch {
          // Fall through to canvas fallback.
        }

        if (liveImg) {
          try {
            if (typeof liveImg.decode === 'function') {
              await liveImg.decode().catch(() => undefined);
            }
            const w = liveImg.naturalWidth || liveImg.width;
            const h = liveImg.naturalHeight || liveImg.height;
            if (w > 0 && h > 0) {
              const canvas = document.createElement('canvas');
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(liveImg, 0, 0, w, h);
                const dataUrl = canvas.toDataURL('image/png');
                if (dataUrl && dataUrl.startsWith('data:image/')) return dataUrl;
              }
            }
          } catch {
            // Cross-origin taint or unsupported source.
          }
        }

        return null;
      };

      for (let i = 0; i < clonedImages.length; i += 1) {
        const img = clonedImages[i];
        const liveImg = liveImages[i] as HTMLImageElement | undefined;
        const src = String(liveImg?.currentSrc || img.getAttribute('src') || '').trim();
        if (!src || src.startsWith('data:')) continue;

        const embedded = await toDataUrlFromLiveImage(liveImg, src);
        if (embedded) {
          img.setAttribute('src', embedded);
          img.removeAttribute('srcset');
          continue;
        }

        // Last fallback: keep original URL.
        img.setAttribute('src', src);
        img.removeAttribute('srcset');
      }

      return `
        <div>
          <h3>${preview.title || ''}</h3>
          <div>${preview.username || ''} | ${preview.category || ''} | ${preview.createdAtUtc || ''}</div>
          <div>${preview.pageUrl ? `Page: ${preview.pageUrl}` : 'Page: -'}</div>
          <hr />
          <div>${root.innerHTML}</div>
        </div>
      `;
    };

    const legacyCopyRich = (html: string): boolean => {
      if (typeof document === 'undefined') return false;
      const host = document.createElement('div');
      host.setAttribute('contenteditable', 'true');
      host.style.position = 'fixed';
      host.style.left = '-99999px';
      host.style.top = '0';
      host.style.opacity = '0';
      host.innerHTML = html;
      document.body.appendChild(host);

      const selection = window.getSelection();
      if (!selection) {
        document.body.removeChild(host);
        return false;
      }

      selection.removeAllRanges();
      const range = document.createRange();
      range.selectNodeContents(host);
      selection.addRange(range);

      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }

      selection.removeAllRanges();
      document.body.removeChild(host);
      return ok;
    };

    const plainText = [
      preview.title || '',
      `${preview.username || ''} | ${preview.category || ''} | ${preview.createdAtUtc || ''}`,
      preview.pageUrl ? `Page: ${preview.pageUrl}` : 'Page: -',
      '',
      stripHtml(preview.html || ''),
    ]
      .filter((line) => line != null && String(line).trim().length > 0)
      .join('\n');

    const htmlContent = await buildRichHtmlWithEmbeddedImages();

    try {
      const clipboardApi = navigator.clipboard as Clipboard & {
        write?: (data: ClipboardItem[]) => Promise<void>;
      };
      let copied = false;

      if (typeof clipboardApi.write === 'function' && typeof window.ClipboardItem !== 'undefined') {
        const clipboardPayload: Record<string, Blob> = {
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
        };
        const item = new ClipboardItem(clipboardPayload);
        await clipboardApi.write([item]);
        copied = true;
      } else {
        copied = legacyCopyRich(htmlContent);
        if (!copied) {
          await navigator.clipboard.writeText(plainText);
          copied = true;
        }
      }

      setCopyFeedback(copied ? 'Copied (text + images)' : 'Copy failed');
      window.setTimeout(() => setCopyFeedback(null), 1500);
    } catch {
      setCopyFeedback('Copy failed');
      window.setTimeout(() => setCopyFeedback(null), 1800);
    }
  };

  const copyNextPreviewImage = async () => {
    if (!previewContentRef.current) return;

    const images = Array.from(previewContentRef.current.querySelectorAll('img')) as HTMLImageElement[];
    if (images.length === 0) {
      setCopyFeedback('No images found');
      window.setTimeout(() => setCopyFeedback(null), 1500);
      return;
    }

    const normalizeToPngBlob = async (img: HTMLImageElement): Promise<Blob | null> => {
      const src = String(img.currentSrc || img.src || '').trim();
      if (!src) return null;

      try {
        const res = await fetch(src, { mode: 'cors', credentials: 'include', cache: 'force-cache' });
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) return blob;
        }
      } catch {
        // fall through
      }

      try {
        if (typeof img.decode === 'function') {
          await img.decode().catch(() => undefined);
        }
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (w <= 0 || h <= 0) return null;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, w, h);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        return blob;
      } catch {
        return null;
      }
    };

    const total = images.length;
    const index = nextPreviewImageIndex % total;
    const target = images[index];
    const blob = await normalizeToPngBlob(target);
    if (!blob) {
      setCopyFeedback(`Image ${index + 1}/${total} failed`);
      window.setTimeout(() => setCopyFeedback(null), 1700);
      return;
    }

    try {
      const clipboardApi = navigator.clipboard as Clipboard & {
        write?: (data: ClipboardItem[]) => Promise<void>;
      };
      if (typeof clipboardApi.write !== 'function' || typeof window.ClipboardItem === 'undefined') {
        setCopyFeedback('Browser blocks image copy');
        window.setTimeout(() => setCopyFeedback(null), 1800);
        return;
      }

      const mime = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/png';
      await clipboardApi.write([new ClipboardItem({ [mime]: blob })]);
      const next = (index + 1) % total;
      setNextPreviewImageIndex(next);
      setCopyFeedback(`Image ${index + 1}/${total} copied`);
      window.setTimeout(() => setCopyFeedback(null), 1600);
    } catch {
      setCopyFeedback('Copy image failed');
      window.setTimeout(() => setCopyFeedback(null), 1800);
    }
  };

  const userGroupByUsername = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users ?? []) {
      map.set((u.username ?? '').trim().toLowerCase(), (u.userGroup ?? '').trim());
    }
    return map;
  }, [users]);

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const usernameQ = filters.username.trim().toLowerCase();
    const categoryQ = filters.category.trim().toLowerCase();
    const titleQ = filters.title.trim().toLowerCase();

    return list.filter((x) => {
      const username = (x.username ?? '').trim().toLowerCase();
      if (usernameQ && !username.includes(usernameQ)) return false;

      if (categoryQ) {
        const g = (userGroupByUsername.get(username) ?? '').toLowerCase();
        if (!g.includes(categoryQ)) return false;
      }

      if (titleQ) {
        const t = (x.title ?? '').trim().toLowerCase();
        if (!t.includes(titleQ)) return false;
      }

      if (filters.status === 'open' && x.isResolved) return false;
      if (filters.status === 'resolved' && !x.isResolved) return false;

      return true;
    });
  }, [filters.category, filters.status, filters.title, filters.username, items, userGroupByUsername]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const categoryOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.userGroup ?? '').toString().trim())
      .filter((c) => c.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const cellStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    color: colors.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    position: 'sticky',
    top: 0,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    zIndex: 1,
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    textAlign: 'left',
  };

  const setResolvedForSelected = async (isResolved: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.setAdminBugReportsResolved(token, ids, isResolved);
      await refresh({ clearSelection: false });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed updating reports');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = window.confirm(`Delete ${ids.length} selected bug report(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteAdminBugReports(token, ids);
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting reports');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: colors.text, marginBottom: 2 }}>Bug Reports</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.subText }}>View and manage reports sent from auth menu</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('neutral')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            style={buttonStyle('success', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void setResolvedForSelected(true)}
          >
            Mark Resolved
          </button>
          <button
            type="button"
            style={buttonStyle('primary', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void setResolvedForSelected(false)}
          >
            Re-open
          </button>
          <button
            type="button"
            style={buttonStyle('danger', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void deleteSelected()}
          >
            Delete ({selectedIds.size})
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 10, color: colors.danger, fontSize: 13, fontWeight: 800 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
          <input
            value={filters.username}
            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-bug-reports-username-options"
          />
          <datalist id="admin-bug-reports-username-options">
            {usernameOptions.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
          <input
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-bug-reports-category-options"
          />
          <datalist id="admin-bug-reports-category-options">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Title</div>
          <input
            value={filters.title}
            onChange={(e) => setFilters((p) => ({ ...p, title: e.target.value }))}
            style={inputStyle}
            placeholder="Filter by title"
          />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Status</div>
          <select
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value as 'all' | 'open' | 'resolved' }))}
            style={inputStyle}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: colors.radius }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 180 }} />
            <col style={{ width: 120 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={filteredItems.length > 0 && filteredItems.every((x) => selectedIds.has(x.id))}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) filteredItems.forEach((x) => next.add(x.id));
                      else filteredItems.forEach((x) => next.delete(x.id));
                      return next;
                    });
                  }}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </th>
              <th style={headerCellStyle}>ID</th>
              <th style={headerCellStyle}>Status</th>
              <th style={headerCellStyle}>Username</th>
              <th style={headerCellStyle}>Title</th>
              <th style={headerCellStyle}>Created</th>
              <th style={headerCellStyle}>Updated</th>
              <th style={headerCellStyle}>Page URL</th>
              <th style={headerCellStyle}>Preview</th>
            </tr>
          </thead>
          <tbody>
            {(filteredItems ?? []).map((x, idx) => {
              const bg = idx % 2 === 0 ? 'transparent' : colors.hover;
              const isSelected = selectedIds.has(x.id);
              return (
                <tr
                  key={x.id}
                  style={{ backgroundColor: isSelected ? (isDarkMode ? colors.hover : '#EEF2FF') : bg, cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(x.id)) next.delete(x.id);
                      else next.add(x.id);
                      return next;
                    });
                  }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(x.id);
                          else next.delete(x.id);
                          return next;
                        });
                      }}
                      style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={cellStyle}>{x.id}</td>
                  <td style={{ ...cellStyle, color: x.isResolved ? colors.success : colors.danger, fontWeight: 800 }}>
                    {x.isResolved ? 'Resolved' : 'Open'}
                  </td>
                  <td style={cellStyle}>{x.username}</td>
                  <td style={cellStyle} title={x.title}>{x.title}</td>
                  <td style={cellStyle}>{x.createdAtUtc}</td>
                  <td style={cellStyle}>{x.updatedAtUtc}</td>
                  <td style={cellStyle} title={x.pageUrl ?? ''}>{x.pageUrl ?? '—'}</td>
                  <td style={cellStyle}>
                    <button
                      type="button"
                      style={buttonStyle('neutral')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreview(x);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}

            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...cellStyle, color: colors.subText, textAlign: 'center', padding: 18 }}>
                  {loading ? 'Loading…' : 'No items'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 9999,
          }}
          onMouseDown={() => setPreview(null)}
        >
          <div
            style={{
              position: 'relative',
              width: previewMaximized ? 'calc(100vw - 32px)' : `${previewSize.width}px`,
              height: previewMaximized ? 'calc(100vh - 32px)' : `${previewSize.height}px`,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: colors.radius,
              padding: 14,
              color: colors.text,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>{preview.title}</div>
                <div style={{ fontSize: 12, color: colors.subText, marginTop: 2 }}>
                  {preview.username} | {preview.category} | {preview.createdAtUtc}
                </div>
                {copyFeedback && (
                  <div style={{ fontSize: 12, color: copyFeedback === 'Copied' ? colors.success : colors.danger, marginTop: 4 }}>
                    {copyFeedback}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  style={buttonStyle('neutral')}
                  onClick={() => void copyPreviewContent()}
                >
                  Copy
                </button>
                <button
                  type="button"
                  style={buttonStyle('neutral')}
                  onClick={() => void copyNextPreviewImage()}
                  title="For Trello: paste each image, then click again for the next one"
                >
                  Copy Next Image
                </button>
                <button
                  type="button"
                  style={buttonStyle('neutral')}
                  onClick={() => setPreviewMaximized((prev) => !prev)}
                >
                  {previewMaximized ? 'Restore' : 'Maximize'}
                </button>
                <button type="button" style={buttonStyle('neutral')} onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>

            <div ref={previewContentRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingRight: 2 }}>
              <div style={{ marginBottom: 10, fontSize: 12, color: colors.subText }}>
                {preview.pageUrl ? `Page: ${preview.pageUrl}` : 'Page: —'}
              </div>

              <div style={{ border: `1px solid ${colors.border}`, borderRadius: colors.radius, padding: 12 }}>
                <div dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: colors.subText }}>
                Plain text preview: {stripHtml(preview.html).slice(0, 280) || '—'}
              </div>
            </div>

            {!previewMaximized && (
              <div
                role="presentation"
                aria-label="Resize preview window"
                onMouseDown={startPreviewResize}
                style={{
                  position: 'absolute',
                  right: 6,
                  bottom: 6,
                  width: 18,
                  height: 18,
                  cursor: 'nwse-resize',
                  borderRight: `2px solid ${colors.subText}`,
                  borderBottom: `2px solid ${colors.subText}`,
                  opacity: 0.75,
                }}
                title="Drag to resize"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBugReportsScreen;
